import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { coupleMonogram, type CoupleNameParts } from '@vendorops/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ImageRejected, prepareCouplePhoto } from '@/lib/image-downscale';
import CouplePhoto from '@/components/CouplePhoto';
import CouplePhotoTarget from '@/components/CouplePhotoTarget';

/**
 * The couple's portrait while the wedding is still being created.
 *
 * **Holds a value; saves nothing.** This is the opposite contract to
 * `CouplePhotoField` next door, and deliberately a second component rather than
 * a flag on that one: there, the wedding exists and uploading on pick is the
 * honest reading of a preview that changes in front of the planner. Here there
 * is no wedding to attach a photo to yet, so the file waits in the dialog and
 * goes up with everything else on Create. One form, one Save — see the create
 * route's own comment for the other half of it.
 *
 * The resize still happens on pick rather than on submit. It is the slow step,
 * and running it while the planner is still filling in the venue costs them
 * nothing; running it after they press Create would add a second of nothing
 * happening to the one moment they are waiting on.
 */
export default function CouplePhotoPicker({
  value,
  onChange,
  couple,
}: {
  value: File | null;
  onChange: (file: File | null) => void;
  couple: CoupleNameParts;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // Tied to the file rather than created at pick time, so the one path that
  // clears the photo from outside this component — the dialog resetting the form
  // after a successful create — releases the URL too.
  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    try {
      const prepared = await prepareCouplePhoto(file);
      onChange(prepared.file);
    } catch (err) {
      // Inline, under the field, because nothing has been saved and nothing has
      // failed to save: this is the form telling the planner about a file, the
      // same way the label field tells them about a name.
      setProblem(
        err instanceof ImageRejected ? err.message : 'That photo could not be read. Try another.',
      );
      onChange(null);
    }
  };

  const clear = () => {
    setProblem(null);
    onChange(null);
  };

  return (
    <div className="space-y-3">
      <Label htmlFor="new-wedding-photo">Photo</Label>
      <div className="flex items-center gap-4">
        {/* Two ways in, on purpose. The picture is the target a planner reaches
            for once they know it works, and the badge plus the scrim on hover
            say that it does; the button beside it is what makes the field
            legible on first use, when the circle still reads as an avatar
            rather than as a control. `CouplePhotoTarget` owns both of those
            signals, shared with the hub header's portrait. */}
        <CouplePhotoTarget
          size="lg"
          label={value ? 'Replace the couple photo' : 'Add a couple photo'}
          onClick={() => inputRef.current?.click()}
          className="w-24 shrink-0"
        >
          <CouplePhoto src={preview} monogram={coupleMonogram(couple)} alt="The couple" />
        </CouplePhotoTarget>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
            >
              <ImagePlus className="size-4" />
              {value ? 'Replace' : 'Add photo'}
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={clear}>
                <Trash2 className="size-4" />
                Remove
              </Button>
            )}
          </div>
          {problem && <p className="text-xs text-destructive">{problem}</p>}
        </div>
      </div>

      <input
        ref={inputRef}
        id="new-wedding-photo"
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          // Cleared so picking the *same* file again after a refusal still fires
          // a change event.
          e.target.value = '';
        }}
      />
    </div>
  );
}
